import {currentAuth, type AuthSnapshot} from './auth.js';
import {renderMarketingPage} from './marketingPage.js';
import {renderAuth, renderLegacyNotice} from './pages.js';
import {currentRoute} from './router.js';
import {renderEnterpriseAccountPage, renderEnterpriseSpacePage, renderExternalIntegrationsPage, renderOrganizationPage, renderProfilePage, renderSpacePage, renderTaskPage, renderTeamJoinPage} from './core/system-pages/index.js';
import {renderDeclarationPage} from './modules/customs-recognition/index.js';
import {renderInvoicePage} from './modules/invoice-recognition/index.js';
import {renderMatchingPage} from './modules/matching/index.js';
import {renderPlanPage, renderLitigationPlanEditorPage, renderLedgerPage} from './modules/ledger-export/index.js';
import {renderArchivePage, renderRebatePage, renderSupplierPage} from './modules/finance-management/index.js';
import {renderLegalContractReviewPage, renderLitigationLedgerPage} from './modules/legal-compliance/index.js';
import {refreshProfessionalModuleAccess, isBusinessFeatureEnabled} from './core/moduleAccess.js';
import {renderBillingPage} from './billingPage.js';

const root = document.querySelector<HTMLElement>('#root');
if (!root) throw new Error('网站根节点不存在');

function isValidAuth(value: AuthSnapshot|null): value is AuthSnapshot {
  return Boolean(value?.user?.id && value.organization?.id && value.organization?.name);
}

async function render(): Promise<void> {
  const route = currentRoute();
  if (route.path === '/tax-assistant') {
    window.history.replaceState({}, '', '/tax-assistant/tasks');
    await render();
    return;
  }
  if (route.path === '/tax-assistant/team-space') {
    window.history.replaceState({}, '', '/tax-assistant/enterprise-space');
    await render();
    return;
  }
  if (route.path === '/admin' || route.path === '/admin/login') {
    window.location.assign('https://admin.xiaostorm.com/');
    return;
  }
  if (route.path === '/') {
    const auth = await currentAuth();
    if (isValidAuth(auth)) { window.history.replaceState({}, '', '/tax-assistant/tasks'); await render(); return; }
    renderMarketingPage(root!);
    return;
  }
  if (route.path === '/login' || route.path === '/register' || route.path === '/forgot-password') {
    const auth = await currentAuth();
    if (isValidAuth(auth)) { window.history.replaceState({}, '', '/tax-assistant/tasks'); await render(); return; }
    renderAuth(root!, route.path === '/register' ? 'register' : route.path === '/forgot-password' ? 'reset' : 'login', 'user');
    return;
  }
  const auth = await currentAuth();
  if (!isValidAuth(auth)) { window.history.replaceState({}, '', '/login'); renderAuth(root!, 'login', 'user'); return; }
  const moduleAccess=await refreshProfessionalModuleAccess();
  const isBusinessRoute=['declarations','invoices','matching','ledger','suppliers','rebate','archive'].includes(route.moduleId);
  if(isBusinessRoute&&!moduleAccess['finance-management']){window.history.replaceState({},'', '/tax-assistant/tasks');await renderTaskPage(root!,auth);return;}
  if(route.moduleId==='plans'){
    const canFinancePlans=moduleAccess['finance-management'];
    const canLitigationPlans=moduleAccess['legal-compliance-assistant']&&isBusinessFeatureEnabled('legal-litigation-ledger-v1');
    if(!canFinancePlans&&!canLitigationPlans){window.history.replaceState({},'', '/tax-assistant/tasks');await renderTaskPage(root!,auth);return;}
  }
  if(route.moduleId==='legal-review'&&!moduleAccess['legal-compliance-assistant']){window.history.replaceState({},'', '/tax-assistant/tasks');await renderTaskPage(root!,auth);return;}
  if(route.moduleId==='legal-litigation'&&!moduleAccess['legal-compliance-assistant']){window.history.replaceState({},'', '/tax-assistant/tasks');await renderTaskPage(root!,auth);return;}
  if (route.moduleId === 'tasks') { await renderTaskPage(root!, auth); return; }
  if (route.moduleId === 'declarations') { await renderDeclarationPage(root!, auth); return; }
  if (route.moduleId === 'invoices') { await renderInvoicePage(root!, auth); return; }
  if (route.moduleId === 'matching') { await renderMatchingPage(root!, auth); return; }
  if (route.moduleId === 'plans') { await renderPlanPage(root!, auth); return; }
  if (route.moduleId === 'ledger') { await renderLedgerPage(root!, auth); return; }
  if (route.moduleId === 'suppliers') { renderSupplierPage(root!, auth); return; }
  if (route.moduleId === 'rebate') { renderRebatePage(root!, auth); return; }
  if (route.moduleId === 'archive') { renderArchivePage(root!, auth); return; }
  if (route.moduleId === 'profile') { renderProfilePage(root!, auth); return; }
  if (route.moduleId === 'space') { await renderSpacePage(root!, auth); return; }
  if (route.moduleId === 'enterprise-space') { renderEnterpriseSpacePage(root!, auth); return; }
  if (route.moduleId === 'team-join') { void renderTeamJoinPage(root!, auth); return; }
  if (route.moduleId === 'integrations') { await renderExternalIntegrationsPage(root!, auth); return; }
  if (route.moduleId === 'organization-settings') { await renderOrganizationPage(root!, auth); return; }
  if (route.moduleId === 'enterprise-account') { await renderEnterpriseAccountPage(root!, auth); return; }
  if (route.moduleId === 'billing') { void renderBillingPage(root!, auth); return; }
  if (route.moduleId === 'legal-review') { await renderLegalContractReviewPage(root!, auth); return; }
  if (route.moduleId === 'legal-litigation') {
    if (route.path === '/tax-assistant/legal-litigation/settings') {
      window.history.replaceState({}, '', '/tax-assistant/plans?kind=legal-litigation');
      await renderPlanPage(root!, auth);
    } else if (route.path === '/tax-assistant/legal-litigation/plan' || route.path.startsWith('/tax-assistant/legal-litigation/plan/')) {
      await renderLitigationPlanEditorPage(root!, auth);
    } else {
      await renderLitigationLedgerPage(root!, auth);
    }
    return;
  }
  renderLegacyNotice(root!, auth);
}

window.addEventListener('popstate', () => {void render();});

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (char) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'}[char] || char));
}

function renderFatalError(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  root!.innerHTML = `<main class="fatal-page"><section class="fatal-card"><h1>页面暂时无法加载</h1><p>${escapeHtml(message)}</p><div class="fatal-actions"><button class="btn-primary" id="fatalRetry">重新加载</button><a class="btn-secondary" href="/login">返回登录</a></div></section></main>`;
  root!.querySelector<HTMLButtonElement>('#fatalRetry')?.addEventListener('click', () => window.location.reload());
}

void render().catch(renderFatalError);
